/**
 * 图片压缩工具
 * 使用 compressorjs 实现图片压缩
 */
import Compressor from 'compressorjs';

export interface CompressOptions {
  /** 压缩质量 0-1，默认 0.6 (60%) */
  quality?: number;
  /** 最大宽度，默认不限制 */
  maxWidth?: number;
  /** 最大高度，默认不限制 */
  maxHeight?: number;
  /** 输出格式，默认 'image/jpeg' */
  mimeType?: string;
}

/**
 * 检查文件是否为图片
 */
export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/');
};

/**
 * 将文件大小转换为 MB
 */
export const bytesToMB = (bytes: number): number => {
  return bytes / (1024 * 1024);
};

/**
 * 压缩图片文件
 * @param file 原始图片文件
 * @param options 压缩选项
 * @returns 压缩后的 File 对象
 */
export const compressImage = async (
  file: File,
  options: CompressOptions = {}
): Promise<File> => {
  const {
    quality = 0.6,
    maxWidth,
    maxHeight,
    mimeType = 'image/jpeg',
  } = options;

  return new Promise((resolve, reject) => {
    new Compressor(file, {
      quality,
      maxWidth,
      maxHeight,
      mimeType,
      checkOrientation: true,  // 自动处理图片旋转
      retainExif: false,       // 不保留 EXIF 信息

      success(result: Blob) {
        // 检查压缩比例
        const compressionRatio = result.size / file.size;

        // 调试日志
        console.log('圖片壓縮資訊:', {
          原始檔案名: file.name,
          原始格式: file.type,
          原始大小: `${(file.size / 1024 / 1024).toFixed(2)}MB`,
          壓縮後大小: `${(result.size / 1024).toFixed(2)}KB`,
          壓縮品質: quality,
          輸出格式: mimeType,
          壓縮比例: `${(compressionRatio * 100).toFixed(1)}%`,
          自動旋轉: '已啟用',
        });

        // 如果压缩比例异常（小于5%），警告用户
        if (compressionRatio < 0.05) {
          console.warn('警告：圖片壓縮比例異常，可能影響圖片品質');
        }

        // 创建新的 File 对象
        const compressedFile = new File([result], file.name, {
          type: result.type,
          lastModified: Date.now(),
        });

        resolve(compressedFile);
      },

      error(err: Error) {
        console.error('圖片壓縮失敗:', err.message);
        reject(err);
      },
    });
  });
};

/**
 * 检查图片文件大小是否超过限制
 * @param file 文件对象
 * @param maxSizeMB 最大大小（MB），默认 10MB
 * @returns 是否超过限制
 */
export const isImageOverSize = (file: File, maxSizeMB: number = 10): boolean => {
  if (!isImageFile(file)) {
    return false;
  }
  return bytesToMB(file.size) > maxSizeMB;
};
